import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Link,
    DialogContentText,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";
import categoryFields from "../shared/categoryFields";
import config from "../config";
import FileUpload from "../components/FileUpload";
import themeColors from "../theme";
import { useAuth } from "../context/AuthContext";

const categoriesWithoutDate = [
    "Board/Medical Certifications",
    "Conferences",
    "Medical Society Membership Dues",
    "Medical License",
];

const asBool = (v) => v === true || v === "true" || v === 1 || v === "1";

// Extract year from purchase date
const getPurchaseYear = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    return d.getUTCFullYear();
};

export default function AddEditPurchaseModal({ open, handleClose, refresh, initialData }) {
    const { token } = useAuth();
    const [editedPurchase, setEditedPurchase] = useState(null);
    const [autoInput, setAutoInput] = useState({}); // key -> inputValue
    const [yearConfirm, setYearConfirm] = useState({
        open: false,
        purchaseYear: null,
    });

    useEffect(() => {
        if (initialData) {
            setEditedPurchase({
                ...initialData,
                metadata: initialData.metadata || {},
                submittedForReimbursement: !!initialData.submittedForReimbursement,
                file: null, // replacement file (if chosen)
            });
        }
    }, [initialData]);

    const handleSave = async (skipYearCheck = false) => {
        if (!editedPurchase?.category) {
            alert("Please select a category.");
            return;
        }

        // Check if purchase date is outside current year
        const currentYear = new Date().getFullYear();
        if (!skipYearCheck && editedPurchase.dateOfPurchase && !categoriesWithoutDate.includes(editedPurchase.category)) {
            const purchaseYear = getPurchaseYear(editedPurchase.dateOfPurchase);
            if (purchaseYear !== currentYear) {
                setYearConfirm({ open: true, purchaseYear });
                return;
            }
        }

        try {
            const formData = new FormData();
            formData.append("category", editedPurchase.category);
            formData.append("cost", editedPurchase.cost);
            formData.append(
                "submittedForReimbursement",
                String(editedPurchase.submittedForReimbursement)
            );
            if (editedPurchase.name) formData.append("name", editedPurchase.name);

            if (!categoriesWithoutDate.includes(editedPurchase.category)) {
                formData.append("dateOfPurchase", editedPurchase.dateOfPurchase);
            }

            // If user selected a new file, send it (replaces existing on backend)
            if (editedPurchase.file instanceof File) {
                formData.append("file", editedPurchase.file);
            }

            const isRecurring = asBool(editedPurchase.metadata?.recurring);

            // Append all metadata EXCEPT recurring keys (we’ll add them explicitly next)
            Object.entries(editedPurchase.metadata || {}).forEach(([key, value]) => {
                if (key === "submittedForReimbursement") return;
                if (key === "recurring" || key === "recurringCycleMonths") return;
                if (value === undefined || value === null) return;
                if (value === "" && typeof value !== "boolean") return;
                formData.append(key, typeof value === "boolean" ? String(value) : value);
            });

            // Always send 'recurring' explicitly so unchecking persists
            formData.append("recurring", isRecurring ? "true" : "false");

            // Only send the cycle if checked; omitting it causes the backend to drop the old value
            if (isRecurring) {
                const cycle = editedPurchase.metadata?.recurringCycleMonths;
                if (cycle !== undefined && cycle !== null && cycle !== "") {
                    formData.append("recurringCycleMonths", String(cycle));
                }
            }

            await axios.put(`${config.SERVER_URL}/api/purchases/${editedPurchase._id}`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            });

            refresh();
            handleClose();
        } catch (err) {
            alert("Failed to update purchase.");
            console.error(err);
        }
    };

    if (!editedPurchase) return null;

    const today = new Date();
    const localToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
        .toISOString()
        .split("T")[0];

    const isRecurringChecked = asBool(editedPurchase.metadata?.recurring);

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Purchase</DialogTitle>

            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                <TextField
                    select
                    label="Category"
                    value={editedPurchase.category}
                    onChange={(e) => setEditedPurchase({ ...editedPurchase, category: e.target.value })}
                    fullWidth
                >
                    {Object.keys(categoryFields)
                        .sort()
                        .map((c) => (
                            <MenuItem key={c} value={c}>
                                {c}
                            </MenuItem>
                        ))}
                </TextField>

                {editedPurchase.category &&
                    categoryFields[editedPurchase.category]?.map((field) => {
                        const { key, label, type, options, freeSoloAllowed = false } = field;

                        if (key === "dateOfPurchase" && categoriesWithoutDate.includes(editedPurchase.category))
                            return null;

                        if (type === "autocomplete") {
                            const opts = options || [];
                            const currentVal =
                                key === "name" ? editedPurchase.name || "" : editedPurchase.metadata?.[key] || "";

                            // If the saved value isn't in options, auto-allow editing via freeSolo
                            const allowFreeSolo = freeSoloAllowed || !opts.includes(currentVal);

                            const setVal = (next) => {
                                if (key === "name") {
                                    setEditedPurchase((prev) => ({ ...prev, name: next }));
                                } else {
                                    setEditedPurchase((prev) => ({
                                        ...prev,
                                        metadata: { ...prev.metadata, [key]: next },
                                    }));
                                }
                            };

                            const inputVal = autoInput[key] ?? (currentVal || "");

                            return (
                                <Autocomplete
                                    key={key}
                                    fullWidth
                                    options={opts}
                                    freeSolo={allowFreeSolo}
                                    value={currentVal || null}
                                    inputValue={inputVal}
                                    isOptionEqualToValue={(opt, v) => opt === v}
                                    onInputChange={(_, newInput) => {
                                        setAutoInput((prev) => ({ ...prev, [key]: newInput || "" }));
                                        if (allowFreeSolo) setVal(newInput || "");
                                    }}
                                    onChange={(_, newValue) => {
                                        const finalVal = newValue || "";
                                        setVal(finalVal);
                                        setAutoInput((prev) => ({ ...prev, [key]: finalVal }));
                                    }}
                                    renderInput={(params) => <TextField {...params} label={label} fullWidth />}
                                />
                            );
                        }

                        // Checkbox fields (e.g., "recurring")
                        if (type === "checkbox") {
                            const checked = asBool(editedPurchase.metadata?.[key]);
                            return (
                                <FormControlLabel
                                    key={key}
                                    control={
                                        <Checkbox
                                            checked={!!checked}
                                            onChange={(e) => {
                                                const v = e.target.checked;
                                                setEditedPurchase((prev) => {
                                                    const next = {
                                                        ...prev,
                                                        metadata: { ...prev.metadata, [key]: v },
                                                    };
                                                    // If unchecking recurring, clear the cycle so the dropdown vanishes and we don't submit it
                                                    if (key === "recurring" && !v) {
                                                        delete next.metadata.recurringCycleMonths;
                                                    }
                                                    return next;
                                                });
                                            }}
                                        />
                                    }
                                    label={label}
                                />
                            );
                        }

                        // Select (dropdown) fields (e.g., "recurringCycleMonths")
                        if (type === "select") {
                            // Only show the cycle dropdown when recurring is checked
                            if (key === "recurringCycleMonths" && !isRecurringChecked) return null;

                            const value = editedPurchase.metadata?.[key] ?? "";
                            return (
                                <TextField
                                    key={key}
                                    select
                                    label={label}
                                    value={value}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setEditedPurchase((prev) => ({
                                            ...prev,
                                            metadata: { ...prev.metadata, [key]: v },
                                        }));
                                    }}
                                    fullWidth
                                >
                                    {(options || []).map((opt) => (
                                        <MenuItem key={opt} value={opt}>
                                            {opt}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            );
                        }

                        // default text/number/date
                        const inputProps =
                            key === "cost"
                                ? { min: 0 }
                                : key === "dateOfPurchase"
                                    ? { max: localToday }
                                    : undefined;

                        const value =
                            key === "name"
                                ? editedPurchase.name
                                : key === "cost"
                                    ? editedPurchase.cost
                                    : key === "dateOfPurchase"
                                        ? (editedPurchase.dateOfPurchase || editedPurchase.metadata?.dateOfPurchase || "")?.slice(0, 10)
                                        : editedPurchase.metadata?.[key] || "";

                        return (
                            <TextField
                                key={key}
                                label={label}
                                type={type}
                                value={value}
                                InputLabelProps={type === "date" ? { shrink: true } : undefined}
                                inputProps={inputProps}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (key === "name") {
                                        setEditedPurchase((prev) => ({ ...prev, name: val }));
                                    } else if (key === "cost") {
                                        if (val === "" || (!isNaN(val) && parseFloat(val) >= 0)) {
                                            setEditedPurchase((prev) => ({ ...prev, cost: val }));
                                        }
                                    } else if (key === "dateOfPurchase") {
                                        setEditedPurchase((prev) => ({ ...prev, dateOfPurchase: val }));
                                    } else {
                                        setEditedPurchase((prev) => ({
                                            ...prev,
                                            metadata: { ...prev.metadata, [key]: val },
                                        }));
                                    }
                                }}
                                fullWidth
                            />
                        );
                    })}

                {/* Existing file (if any) */}
                {editedPurchase?.fileUrl && (
                    <Box>
                        <Link href={editedPurchase.fileUrl} target="_blank" sx={{ color: "#007F7E" }}>
                            Download current attachment
                        </Link>
                    </Box>
                )}

                {/* Replacement upload (validated: ≤5MB and PDF/JPEG/PNG) */}
                {editedPurchase.category && (
                    <FileUpload
                        label={editedPurchase?.fileUrl ? "Replace attachment" : "Attach receipt"}
                        multiple={false}
                        onFilesChange={(arr) => {
                            const file = arr?.[0] || null;
                            setEditedPurchase((prev) => ({ ...prev, file }));
                        }}
                    />
                )}

                {editedPurchase.category && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={editedPurchase.submittedForReimbursement}
                                onChange={(e) =>
                                    setEditedPurchase((prev) => ({
                                        ...prev,
                                        submittedForReimbursement: e.target.checked,
                                    }))
                                }
                            />
                        }
                        label="Has this been submitted for reimbursement?"
                    />
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    variant="contained"
                    sx={{ backgroundColor: "#007F7E", color: "#fff" }}
                    onClick={() => handleSave()}
                >
                    Save
                </Button>
            </DialogActions>
            </Dialog>

            {/* YEAR CONFIRMATION */}
            <Dialog
                open={yearConfirm.open}
                onClose={() => setYearConfirm({ open: false, purchaseYear: null })}
            >
                <DialogTitle>Purchase Date Outside Current Year</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Your purchase date is outside of the current calendar year. Please note this purchase will not be deducted from this year's annual CME fund.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setYearConfirm({ open: false, purchaseYear: null })}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ backgroundColor: themeColors.teal, color: "#fff" }}
                        onClick={() => {
                            setYearConfirm({ open: false, purchaseYear: null });
                            handleSave(true);
                        }}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}